import { apiSlice } from "api/apiSlice";

import {
    createSelector,
    createEntityAdapter,
} from "@reduxjs/toolkit";

const formatter = currency => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  
    // These options are needed to round to whole numbers if that's what you want.
    // minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    // maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});
const sizeFormatter = Intl.NumberFormat('en', { notation: 'compact' });

const productAdapter = createEntityAdapter({})

const initialState = productAdapter.getInitialState({
    productsDetails : {
        page: 0,
        filters: {},
        productsPerpage: 20,
        total_results: 0
    },
})

export const productApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getProduct: builder.query({
            query: params => ({ url: `products`, params }),
            validateStatus: (response, result) => response.status === 200 && !result.isError,
            transformResponse: responseData => {
                const loadedProduct = responseData.products.map(product => {
                    const newProduct = product;
                    /* eslint-disable */
                    if(responseData.filters?.distri_userId || responseData.filters?.distributerId){
                        newProduct.inStock   =  Boolean(Number(product.dist_inStock) === 1) ;
                        newProduct.priceShow = {
                            original : formatter('DZD').format(product.dist_price),
                            sold : null
                        }
                        newProduct.progress = Number( 100 - Number(Number(product.sold)  * 100 / Number(product.dist_quantity) ).toFixed())
                        newProduct.quantityShow = sizeFormatter.format(Number(product.dist_quantity) - Number(product.sold))
                    }else{
                        newProduct.inStock   =  Boolean(Number(product.inStock) === 1) ;
                        newProduct.priceShow = {
                            original : formatter('DZD').format(Number(product.price.original)),
                            sold : formatter('DZD').format(product.price.sold)
                        }
                        newProduct.progress =  !isNaN(product.statistics?.quantity) ? Number( 100 - Number(Number(product.statistics?.sold)  * 100 / Number(product.statistics?.quantity) ).toFixed()) :0;
                        newProduct.quantityShow = !isNaN(product.statistics?.quantity) ? sizeFormatter.format(Number(product.statistics?.quantity) - Number(product.statistics?.sold)) :0;
                    }
                    return newProduct;
                });

                initialState.productsDetails = {
                    ...initialState.productsDetails,
                        page: responseData.page,
                        filters: responseData.filters ,
                        productsPerpage: responseData.productsPerpage,
                        total_results: responseData.total_results
                }

                return productAdapter.setAll(initialState, loadedProduct)     
            },
            /* eslint-disable */
            providesTags: (result, error, id) => // [{ type: 'Product', arg }]
             {
                /* eslint-disable */
                if (result?.ids) {
                    return [
                        { type: 'Product', id: 'PARTIAL-LIST' },
                        ...result.ids.map(id => ({ type: 'Product', id }))
                    ]
                } else return [{ type: 'Product', id: 'PARTIAL-LIST' }]
            }
        }),
        getUserProduct: builder.query({
            query: (id) => ({ url: `userProduct/${id}` }),
            validateStatus: (response, result) => response.status === 200 && !result.isError,
            transformResponse: responseData => responseData,
            /* eslint-disable */
            providesTags: (result, error, id) => // [{ type: 'Product', arg }]
             {
                /* eslint-disable */
                if (result?.ids) {
                    return [
                        { type: 'Product', id: 'PARTIAL-LIST' },
                        ...result.ids.map(id => ({ type: 'Product', id }))
                    ]
                } else return [{ type: 'Product', id: 'PARTIAL-LIST' }]
            }
        }),
        getProductById: builder.query({
            query: (id) => ({ url: `/product/${id}` }),
            validateStatus: (response, result) => response.status === 200 && !result.isError,
            transformResponse: responseData => {
                            const loadedProduct = responseData;
                            loadedProduct.inStock   = Boolean(Number(loadedProduct.inStock) === 1) ;
                            loadedProduct.priceShow = {
                                    original : formatter('DZD').format(loadedProduct.price.original),
                                    sold : formatter('DZD').format(loadedProduct.price.sold)
                            }
                            loadedProduct.quantityShow = sizeFormatter.format(loadedProduct.quantity - (loadedProduct.statistics?.sold || 0))
                    return loadedProduct
            },
            /* eslint-disable */
            providesTags: (result, error, id) => [{ type: 'Product', id }],
        }),
        getCSVFile: builder.mutation({
            query: initialProductData => ({ 
                url: `/generate-csv`,
                method: 'POST',
                body: {
                    ...initialProductData,
                }
            }),
            /* eslint-disable */
        }),
        uploadCSVFile: builder.mutation({
            query: initialProductData => ({ 
                url: `/upload-csv`,
                method: 'POST',
                body: initialProductData
            }),
            /* eslint-disable */
        }),
        rateProduct: builder.mutation({
            query: initialProductData => ({
                    url: '/productRating',
                    method: 'PATCH',
                    body: {
                            ...initialProductData,
                    }
            }),
            invalidatesTags: (result, error, arg) => [
                    { type: 'Product', id: arg.id }
            ]
        }),
        reactToProduct: builder.mutation({
            query: initialReviewData => ({
                url: '/productReact',
                method: 'PATCH',
                body: {
                    ...initialReviewData,
                }
            }),
            invalidatesTags: (result, error, arg) => {
                // Assuming `arg` contains the `product_id` that needs to be invalidated
                return [{ type: 'Product', id: 'PARTIAL-LIST' },{ type: 'Product', id: arg.id }];
            }
        }),
        getProductReactions: builder.query({
            query: ids => ({ url: `product-reactions`, params : ids }),
            validateStatus: (response, result) => response.status === 200 && !result.isError,
            transformResponse: responseData => {
                            const loadedProduct = responseData;
                            return loadedProduct
            },
            providesTags: (result, error, id) => [{ type: 'Product', id }],
        }),
        addNewProduct: builder.mutation({
            query: initialProductData => ({
                url: '/product',
                method: 'POST',
                body: {
                    ...initialProductData,
                }
            }),
            invalidatesTags: [
                { type: 'Product', id: "LIST" }
            ]
        }),
        updateProduct: builder.mutation({
            query: initialProductData => ({
                url: '/product',
                method: 'PATCH',
                body: {
                    ...initialProductData,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Product', id: arg.id }
            ]
        }),
        updateDistributorProduct: builder.mutation({
            query: initialProductData => ({
                url: '/product-distibutor',
                method: 'PATCH',
                body: {
                    ...initialProductData,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Product', id: arg.update.product_id },
                { type: 'Distributor', id: arg.update.distributer_id }
            ]
        }),
        updateProductPhoto: builder.mutation({
            query: initialProductData => ({
                url: '/product/upload',
                method: 'POST',
                body: initialProductData,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Product', id: arg.id }
            ]
        }),
        deleteProductPhoto: builder.mutation({
            query: initialProductData => ({
                url: '/productUpload',
                method: 'DELETE',
                body: initialProductData,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Product', id: arg.id },
                { type: 'Product', id: 'PARTIAL-LIST' },
            ]
        }),
        deleteProduct: builder.mutation({
            query: ({ id }) => ({
                url: `/product`,
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Product', id: arg.id },
                { type: 'Product', id: 'PARTIAL-LIST' }
            ]
        }),
        uploadProductSheet: builder.mutation({
            query: initialProductData => ({
                    url: '/product/sheet',
                    method: 'POST',
                    body: initialProductData,
            }),
            invalidatesTags: (result, error, arg) => [
                    { type: 'Product', id: arg.id }
            ]
        }),
        getProductSheetById: builder.query({
            query: (id) => ({ url: `product-sheet/${id}` }),
            validateStatus: (response, result) => response.status === 200 && !result.isError,
            transformResponse: responseData => {
                            const loadedSheet = responseData;
                            return loadedSheet
            },
            /* eslint-disable */
            providesTags: (result, error, id) => [{ type: 'Product', id }],
        }),
        createProductSheet: builder.mutation({
            query: initialProductData => ({
                    url: '/product/create-sheet',
                    method: 'POST',
                    body: initialProductData,
            }),
            invalidatesTags: (result, error, arg) => [
                    { type: 'Product', id: arg.id }
            ]
        }),
        
    }),
})

export const {
    useGetProductQuery,
    useLazyGetProductQuery,
    useGetProductByIdQuery,
    useLazyGetProductByIdQuery,
    useGetUserProductQuery,
    useGetProductSheetByIdQuery,
    useLazyGetProductReactionsQuery,
    useAddNewProductMutation,
    useUpdateProductMutation,
    useUpdateDistributorProductMutation,
    useUpdateProductPhotoMutation,
    useDeleteProductPhotoMutation,
    useDeleteProductMutation,
    useRateProductMutation,
    useReactToProductMutation,
    useGetCSVFileMutation,
    useUploadCSVFileMutation,
    useUploadProductSheetMutation,
    useCreateProductSheetMutation
} = productApiSlice

// returns the query result object
export const selectProductResult = productApiSlice.endpoints.getProduct.select()

// creates memoized selector
const selectProductData = createSelector(
    selectProductResult,
    productResult => productResult.data // normalized state object with ids & entities
)
//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllProducts,
    selectById: selectProductById,
    selectIds: selectProductIds
    // Pass in a selector that returns the product slice of state
} = productAdapter.getSelectors(state => selectProductData(state) ?? initialState)

export const selectProductByUser = createSelector(
    [selectAllProducts,(state, userId) => userId],
    (products, userId) => products.find(product => product.userId === userId)
);
